import React from "react";

import clsx from "clsx";

import { type FetchResult, type MutationResult } from "@apollo/client";
import { useFeatureFlags, useNorthbeamMutation } from "@hooks/http";
import { useDisclosure } from "@hooks/use-disclosure";
import { HTTP_METHOD, useRestApi } from "@hooks/use-rest-api";
import {
  type DeleteConnection,
  type SetShopifySettings,
  type SetShopifySettingsVariables,
} from "@north-beam/nb-client-side";
import { type ConnectionIssue } from "@north-beam/nb-common";
import { PrimaryButton } from "@shared/buttons";
import Spinner from "@shared/spinner";

import ShopifyPixel, {
  type ShopifyPixelConnectionType,
} from "./connections/shopify/pixel";
import { EditSettingsModal } from "./connections/shopify/settings-modal";
import { SET_SHOPIFY_SETTINGS } from "./queries";

type ShopifyStatusLineProps = {
  connectionIssue?: ConnectionIssue | null;
  shopUrl: string;
  destroyConnection: () => Promise<
    FetchResult<DeleteConnection, Record<string, any>, Record<string, any>>
  >;
  destroyInfo: MutationResult<DeleteConnection>;
};

export default function ShopifyStatusLine({
  connectionIssue,
  shopUrl,
  destroyConnection,
  destroyInfo,
}: ShopifyStatusLineProps) {
  const { hasFeatureFlag } = useFeatureFlags();
  const shouldRenderShopifyEditSettings = hasFeatureFlag(
    "manageShopifyAppSettings",
  );

  const permissionsHaveUpdated =
    connectionIssue && !connectionIssue.reconnectRecommended;
  let emoji = connectionIssue ? "🚨" : "✔️";
  if (permissionsHaveUpdated) {
    emoji = "ⓘ";
  }

  const { data, loading, error } = useRestApi<ShopifyPixelConnectionType>(
    "/api/shopify-pixel/verify-and-connect",
    {
      method: HTTP_METHOD.POST,
      data: { shopUrl },
    },
  );

  const renderShopifyErrorNotificationMessage = () => {
    return (
      <div>
        <b>Note:</b>{" "}
        <em>
          {"If you are seeing this connection error and you have the northbeam app " +
            "installed, please go to "}
          <a
            href={`https://${shopUrl}/admin`}
            target="_blank"
            rel="noreferrer"
          >{`${shopUrl}/admin`}</a>
          {`,
                select "Apps" in the side navbar, and click on the "Northbeam" app from the dropdown.
                This should put you back through the authorization flow and connect this app permanently.
                `}
        </em>
      </div>
    );
  };

  const renderConnectionIssue = () => {
    return (
      <tr>
        <td colSpan={4}>
          <div
            className="alert alert-warning mt-1"
            style={{ whiteSpace: "pre-line" }}
          >
            <b>Error</b>: <em>{connectionIssue?.message}</em>
            {renderShopifyErrorNotificationMessage()}
          </div>
        </td>
      </tr>
    );
  };

  const renderWebPixelPermissionsMessage = () => (
    <tr>
      <td colSpan={4}>
        <div
          className={clsx(
            "bg-blue-50 border-t-4 border-blue-500 rounded-b",
            "text-blue-900 px-4 py-3 shadow-md",
          )}
          role="alert"
          style={{ whiteSpace: "pre-line" }}
        >
          <p className="font-bold">
            <span className="text-xl text-center mr-2">{emoji}</span>
            Shopify permissions need to be updated
          </p>
          <p className="text-sm">
            We've made improvements to Northbeam's Shopify app.
            {" To take advantage of the latest update, you'll need to grant Northbeam " +
              "permission in Shopify."}
            {" In the interim, your store will remain synced to Northbeam."}
          </p>
          <p className="font-bold">To update Northbeam permissions:</p>
          <ol>
            <li>
              1. Go to{" "}
              <a
                href={`https://${shopUrl}/admin`}
                target="_blank"
                rel="noreferrer"
              >{`${shopUrl}/admin`}</a>
            </li>
            <li>2. Select &quot;Apps&quot; in the side navigation</li>
            <li>3. Click the &quot;Northbeam&quot; app in the dropdown</li>
          </ol>
        </div>
      </td>
    </tr>
  );

  const renderSuffix = () => {
    if (permissionsHaveUpdated) {
      return renderWebPixelPermissionsMessage();
    } else if (connectionIssue) {
      return renderConnectionIssue();
    }
    return (
      <tr>
        <td colSpan={4}>
          {loading ? (
            <Spinner />
          ) : (
            <ShopifyPixel connection={data} connectError={error} />
          )}
        </td>
      </tr>
    );
  };

  const [setShopifySettings] = useNorthbeamMutation<
    SetShopifySettings,
    SetShopifySettingsVariables
  >(SET_SHOPIFY_SETTINGS);
  const [
    editSettingsModalOpened,
    { open: openEditSettingsModal, close: closeEditSettingsModal },
  ] = useDisclosure(false);
  const renderManage = () => {
    if (connectionIssue) {
      return <></>;
    }

    return (
      <>
        <PrimaryButton onClick={openEditSettingsModal} disabled={loading}>
          EDIT SETTINGS
        </PrimaryButton>
        <EditSettingsModal
          shopUrl={shopUrl}
          updateCollectPageViews={async (collectPageViews: boolean) => {
            await setShopifySettings({
              variables: {
                shopUrl,
                collectPageViews,
              },
            });
            if (data) {
              data.collectPageViews = collectPageViews;
            }
          }}
          collectPageViews={data?.collectPageViews ?? false}
          isOpen={editSettingsModalOpened}
          onClose={closeEditSettingsModal}
        ></EditSettingsModal>
      </>
    );
  };

  return (
    <React.Fragment key={shopUrl}>
      <tr className="tr text-small ">
        <td className="td">{emoji}</td>
        <td className="td">{shopUrl}</td>
        <td className="td break-words">{shopUrl}</td>
        <td>
          <PrimaryButton
            variant="danger"
            className="w-24"
            loading={destroyInfo.loading}
            onClick={() => {
              destroyConnection();
            }}
          >
            <i className="fa-regular fa-sharp fa-trash text-white mr-1" />
            DELETE
          </PrimaryButton>
        </td>
        {shouldRenderShopifyEditSettings && <td>{renderManage()}</td>}
      </tr>
      {renderSuffix()}
    </React.Fragment>
  );
}
